import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { Home, PlusCircle, ClipboardList, Trophy, User, Bell } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ForcePasswordDialog } from "@/components/ChangePassword";
import { EarthScreen } from "@/components/Earth";
import { PullToRefresh, InstallPrompt } from "@/components/NativeShell";

const TABS = [
  { to: "/", label: "Home", Icon: Home, id: "home" },
  { to: "/book", label: "Book", Icon: PlusCircle, id: "book" },
  { to: "/history", label: "History", Icon: ClipboardList, id: "history" },
  { to: "/leaderboard", label: "Leaderboard", Icon: Trophy, id: "leaderboard" },
  { to: "/profile", label: "Profile", Icon: User, id: "profile" },
];

export const Logo = ({ light, className = "h-7" }) => (
  <img src={light ? "/logo-white.png" : "/logo.png"} alt="Kindsaver" className={`w-auto ${className}`} />
);

export const Layout = () => {
  const { t, summary, user } = useApp();
  const loc = useLocation();
  const unread = summary?.unread || 0;
  return (
    <div className="mx-auto min-h-screen max-w-md bg-[#F4F8F5]">
      <header className="pt-safe sticky top-0 z-40 flex h-14 items-center justify-between border-b border-[#D2E0D8] bg-[#F4F8F5]/90 px-4 backdrop-blur-md">
        <Link to="/" data-testid="header-logo"><Logo /></Link>
        {!user ? (
          <Link to="/login" data-testid="header-login-btn" className="rounded-full bg-[#0D3B2B] px-4 py-2 text-sm font-bold text-white">{t("Log in")}</Link>
        ) : (
        <Link to="/notifications" className="relative rounded-full p-2 text-[#0D3B2B]" data-testid="notifications-bell" aria-label={t("Notifications")}>
          <Bell className="h-6 w-6" />
          {unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#E05C3A] px-1 text-[11px] font-bold text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Link>
        )}
      </header>
      <PullToRefresh><main key={loc.pathname} className="ks-page px-4 pb-28 pt-4"><Outlet /></main></PullToRefresh>
      <ForcePasswordDialog />
      <InstallPrompt />
      <nav className="pb-safe fixed bottom-0 left-0 right-0 z-50 mx-auto max-w-md border-t border-[#D2E0D8] bg-white/95 backdrop-blur-md" data-testid="bottom-nav">
        <div className="flex h-16 items-stretch justify-around">
          {TABS.map(({ to, label, Icon, id }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${id}`}
              className={({ isActive }) => `flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors ${isActive ? "text-[#0D3B2B]" : "text-[#7B968B]"}`}>
              {({ isActive }) => (
                <>
                  {id === "book" ? (
                    <span className={`-mt-5 flex h-12 w-12 items-center justify-center rounded-full shadow-lg ${isActive ? "bg-[#0D3B2B]" : "bg-[#E05C3A]"}`}>
                      <Icon className="h-6 w-6 text-white" />
                    </span>
                  ) : (
                    <Icon className={`h-6 w-6 ${isActive ? "text-[#2E9E6B]" : ""}`} />
                  )}
                  <span className="max-w-full truncate px-0.5">{t(label)}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export const PageTitle = ({ children, sub }) => (
  <div className="mb-5">
    <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0D3B2B]">{children}</h1>
    {sub && <p className="mt-1 text-sm text-[#4A6358]">{sub}</p>}
  </div>
);

export const Card = ({ className = "", children, ...p }) => (
  <div className={`rounded-3xl border border-[#D2E0D8] bg-white p-5 ${className}`} {...p}>{children}</div>
);

export const BigButton = ({ className = "", coral, children, ...p }) => (
  <button className={`flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-base font-bold text-white shadow-sm transition-transform active:scale-[.97] disabled:opacity-50 ${coral ? "bg-[#E05C3A]" : "bg-[#0D3B2B]"} ${className}`} {...p}>
    {children}
  </button>
);

export const Splash = () => <EarthScreen testid="splash" title="Kind. Clean. Bold." />;
